import { ArrowRight, ExternalLink, Gauge, Radio, Wrench } from "lucide-react"
import { Link } from "react-router"
import { PageHero } from "@/components/PageHero"
import { Reveal } from "@/components/Reveal"
import { SectionHeading } from "@/components/SectionHeading"
import { RemoteImage } from "@/components/RemoteImage"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { livePlatformUrl, products } from "@/data/site"

export default function Products() {
    return (
        <>
            <PageHero
                eyebrow="Smart Drain Products"
                title="Retrofit protection for the places where drains fail first."
                description="The product page now uses the updated inlet-filter and open-drain retrofit renders, including the visible IoT monitoring unit."
            />

            <section className="px-6 py-24 sm:py-32">
                <div className="mx-auto max-w-7xl">
                    <Reveal><SectionHeading eyebrow="Three deployment formats" title="Choose the intervention that matches the drain." description="The product family is designed around existing infrastructure and new builds: roadside inlets, open-drain hotspot retrofits, and purpose-built dual-layer systems for new construction projects." /></Reveal>
                    <div className="mt-14 grid gap-8">
                        {products.map((product, i) => (
                            <Reveal key={product.slug} delay={i * .06}>
                                <div className="grid overflow-hidden rounded-[2rem] border border-slate-200 bg-white lg:grid-cols-[1.1fr_.9fr] dark:border-white/10 dark:bg-white/[.04]">
                                    <div className={i % 2 ? "lg:order-2" : ""}>
                                        <div className="grid h-full gap-3 p-3 sm:grid-cols-2">
                                            <RemoteImage src={product.image} alt={product.title} className="min-h-[22rem] rounded-[1.5rem]" />
                                            <RemoteImage src={product.secondaryImage ?? product.image} alt={`${product.title} alternate`} className="min-h-[22rem] rounded-[1.5rem]" />
                                        </div>
                                    </div>
                                    <div className="p-8 sm:p-10">
                                        <Badge className="text-emerald-800 dark:text-emerald-300">{product.kicker}</Badge>
                                        <h2 className="mt-5 text-4xl font-semibold tracking-[-.05em]">{product.title}</h2>
                                        <p className="mt-5 leading-8 text-slate-600 dark:text-slate-400">{product.description}</p>
                                        <div className="mt-7 grid gap-3">
                                            {product.bullets.map((bullet) => <div key={bullet} className="rounded-2xl bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700 dark:bg-white/[.06] dark:text-slate-300">{bullet}</div>)}
                                        </div>
                                        <div className="mt-8 flex flex-wrap gap-3">
                                            <Link to="/technology"><Button variant="outline">See how the technology works</Button></Link>
                                            <Link to="/start-pilot"><Button>Start a Smart Drain Pilot <ArrowRight className="size-4" /></Button></Link>
                                        </div>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-[#061a1d] px-6 py-24 text-white sm:py-32">
                <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[.9fr_1.1fr] lg:items-center">
                    <Reveal>
                        <SectionHeading light eyebrow="Integrated IoT" title="The physical retrofit is only half the system." description="Integrated sensing supports remote visibility of drain conditions, fill level, water level and maintenance needs so crews can respond based on actual conditions rather than fixed inspection cycles, helping teams prepare and respond before blockages or flooding occur." />
                        <div className="mt-8 grid gap-4 sm:grid-cols-3">{[[Gauge, "Water level"], [Radio, "Remote status"], [Wrench, "Maintenance alerts"]].map(([Icon, label]) => {
                            const I = Icon as typeof Gauge
                            return <div key={String(label)} className="rounded-2xl border border-white/10 bg-white/[.04] p-5"><I className="size-5 text-emerald-200" /><div className="mt-4 text-sm font-semibold">{String(label)}</div></div>
                        })}</div>
                    </Reveal>
                    <Reveal delay={.08}>
                        <Card className="border-white/10 bg-white/[.05] p-8 text-white">
                            <div className="text-xs font-semibold uppercase tracking-[.16em] text-emerald-200">Smart Drain Intelligence</div>
                            <h3 className="mt-4 text-3xl font-semibold tracking-[-.045em]">See the digital monitoring layer.</h3>
                            <p className="mt-4 leading-7 text-white/55">The Smart Drain live platform supports field telemetry, drainage visibility and operational decision support.</p>
                            <a href={livePlatformUrl} target="_blank" rel="noreferrer" className="mt-7 inline-block"><Button>Open smartdrain.info <ExternalLink className="size-4" /></Button></a>
                        </Card>
                    </Reveal>
                </div>
            </section>

            <section className="px-6 py-24 sm:py-32">
                <Reveal className="mx-auto max-w-7xl">
                    <div className="rounded-[2rem] bg-emerald-100 p-8 sm:p-12 dark:bg-emerald-400/10">
                        <SectionHeading eyebrow="Retrofit-first" title="Upgrade the drain. Not the whole network." description="Smart Drain can be adapted to different drainage systems, including kerb inlets, open channels and enclosed networks. Start at the highest-value problem site, validate performance, then scale." />
                        <Link to="/start-pilot" className="mt-8 inline-block"><Button size="lg">Start a Smart Drain Pilot <ArrowRight className="size-4" /></Button></Link>
                    </div>
                </Reveal>
            </section>
        </>
    )
}